import React, { useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Upload,
  Button,
  Radio,
  Badge,
  Space,
  Table,
  message,
  Select,
  Grid,
  Row,
  Col,
  Tag,
  Modal,
  DatePicker,
  Form,
  Spin,
  Input,
} from 'antd';
import {
  ToTopOutlined,
  UserOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CalendarOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { force_sync_ga4_propertiesApi } from '../../api/admin.apis';

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;
const { RangePicker } = DatePicker;
const { useForm } = Form;

// Utility functions for date handling
const parseUTCDate = (dateString) => {
  if (!dateString) return null;
  if (dateString.includes('.')) {
    return new Date(dateString + 'Z');
  }
  return new Date(dateString + 'Z');
};

const formatLocalDate = (dateString) => {
  const date = parseUTCDate(dateString);
  if (!date) return 'Never synced';
  
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Never';
  
  const date = parseUTCDate(dateString);
  if (!date) return 'Never';
  
  const diffMs = new Date() - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
};

// Sync Modal Component
const SyncModal = ({ 
  visible, 
  onCancel, 
  onSubmit, 
  selectedUser, 
  selectedProperty, 
  loading,
  screens 
}) => {
  const [form] = useForm();

  const handleSubmit = async (values) => {
    await onSubmit(values);
    form.resetFields();
  };

  return (
    <Modal
      title={
        <Space>
          <SyncOutlined />
          <span>Force Sync GA4 Property</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button
          key="cancel"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
          icon={<SyncOutlined />}
          disabled={loading}
        >
          Start Sync
        </Button>,
      ]}
      width={screens.xs ? '90%' : 520}
      maskClosable={!loading}
      closable={!loading}
    >
      {selectedUser && selectedProperty && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            date_range: null,
          }}
          disabled={loading}
        >
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Row gutter={8}>
                <Col span={24}>
                  <Space direction="vertical" size={2}>
                    <Text type="secondary" style={{ fontSize: 12 }}>User</Text>
                    <Space>
                      <UserOutlined />
                      <Text strong>{selectedUser.full_name}</Text>
                      <Text type="secondary">({selectedUser.email})</Text>
                    </Space>
                  </Space>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={24}>
                  <Space direction="vertical" size={2}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Property</Text>
                    <Space>
                      <Text strong>{selectedProperty.name}</Text>
                      {selectedProperty.defaultUri !== '-' && (
                        <Text type="secondary">({selectedProperty.defaultUri})</Text>
                      )}
                    </Space>
                  </Space>
                </Col>
              </Row>
            </Space>
          </Card>

          <Form.Item
            name="date_range"
            label={
              <Space>
                <CalendarOutlined />
                <span>Date Range for Sync</span>
              </Space>
            }
            rules={[{ required: true, message: 'Please select a date range' }]}
          >
            <RangePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              placeholder={['Start Date', 'End Date']}
              disabledDate={(current) => {
                return current && current > new Date();
              }}
            />
          </Form.Item>

          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            <InfoCircleOutlined /> This will force a sync of analytics data for the selected date range.
            The process may take several minutes to complete.
          </Paragraph>

          {loading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
              <Paragraph style={{ marginTop: 10 }}>
                Initiating sync... Please wait.
              </Paragraph>
            </div>
          )}
        </Form>
      )}
    </Modal>
  );
};

// Mobile Card Component
const MobileCard = ({ 
  record, 
  selectedProperties, 
  onPropertySelect, 
  onSyncClick,
  formatTimeAgo 
}) => (
  <Card size="small" style={{ marginBottom: 8 }}>
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Row align="middle" gutter={8}>
        <Col>
          <UserOutlined style={{ fontSize: 16 }} />
        </Col>
        <Col flex="auto">
          <Space direction="vertical" size={2}>
            <Text strong>{record.user.full_name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.user.email}</Text>
          </Space>
        </Col>
        <Col>
          <Tag color={record.selectedProperty.selected ? 'green' : 'default'} style={{ margin: 0 }}>
            {record.selectedProperty.selected ? 'Active' : 'Inactive'}
          </Tag>
        </Col>
      </Row>

      <Select
        value={selectedProperties[record.user.id] || record.selectedProperty.id}
        onChange={(propertyId) => onPropertySelect(record.user.id, propertyId)}
        size="small"
        style={{ width: '100%' }}
        placeholder="Select property"
      >
        {record.properties.map((property) => (
          <Select.Option key={property.id} value={property.id}>
            <Space direction="vertical" size={2}>
              <Text>{property.name}</Text>
              {property.defaultUri !== '-' && (
                <Text type="secondary" style={{ fontSize: 11 }}>{property.defaultUri}</Text>
              )}
            </Space>
          </Select.Option>
        ))}
      </Select>

      <Row justify="space-between" align="middle">
        <Col>
          <Space>
            <ClockCircleOutlined style={{ fontSize: 12 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatTimeAgo(record.last_synced_at)}
            </Text>
          </Space>
        </Col>
        <Col>
          <Space>
            {record.selectedProperty.measurementId !== 'N/A' && (
              <Text code style={{ fontSize: 11 }}>
                {record.selectedProperty.measurementId}
              </Text>
            )}
            <Button
              type="link"
              size="small"
              icon={<SyncOutlined />}
              onClick={() => onSyncClick(record.user, record.selectedProperty)}
              style={{ padding: 0 }}
            />
          </Space>
        </Col>
      </Row>
    </Space>
  </Card>
);

// Main ProjectsTable Component
export const ProjectsTable = ({ onFilterChange, ga4Properties }) => {
  const [selectedProperties, setSelectedProperties] = useState({});
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('a'); // 'a' for all, 'b' for active, 'c' for inactive
  const screens = useBreakpoint();
  const [synccompleted, setSyncCompleted] = useState(false);
  const groupPropertiesByUser = () => {
    if (!ga4Properties) return [];

    return ga4Properties.map(userData => {
      const { user, connections } = userData;
      let allProperties = [];
      let latestSyncTime = null;
      let latestSyncTimestamp = null;

      connections.forEach(connection => {
        const { last_synced_at, properties } = connection;
        
        if (last_synced_at) {
          const syncDate = parseUTCDate(last_synced_at);
          if (!latestSyncTime || syncDate > latestSyncTime) {
            latestSyncTime = syncDate;
            latestSyncTimestamp = last_synced_at;
          }
        }
        
        properties.forEach(property => {
          const stream = property.streams?.[0];
          allProperties.push({
            id: property.id,
            name: property.name,
            type: property.type,
            selected: property.selected,
            measurementId: stream?.webStreamData?.measurementId || 'N/A',
            defaultUri: stream?.webStreamData?.defaultUri || '-',
            account_name: connection.account_name,
            connection_id: connection.connection_id,
          });
        });
      });

      allProperties.sort((a, b) => {
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        return a.name.localeCompare(b.name);
      });

      const userSelectedPropertyId =
        user.selected_property_id ||
        allProperties.find(p => p.selected)?.id ||
        allProperties[0]?.id;

      if (!selectedProperties[user.id]) {
        setSelectedProperties(prev => ({
          ...prev,
          [user.id]: userSelectedPropertyId
        }));
      }

      return {
        user: { ...user, selected_property_id: userSelectedPropertyId },
        properties: allProperties,
        last_synced_at: latestSyncTimestamp,
        selectedProperty: allProperties.find(p => p.id === (selectedProperties[user.id] || userSelectedPropertyId)) || allProperties[0],
      };
    });
  };

  const groupedData = groupPropertiesByUser();

  // Filter data based on search text and status filter
  const filteredData = useMemo(() => {
    let filtered = groupedData;

    // Apply status filter
    if (filterStatus === 'b') {
      // Active - users with selected property that is active
      filtered = filtered.filter(record => 
        record.selectedProperty && record.selectedProperty.selected === true
      );
    } else if (filterStatus === 'c') {
      // Inactive - users with selected property that is inactive
      filtered = filtered.filter(record => 
        record.selectedProperty && record.selectedProperty.selected === false
      );
    }

    // Apply search filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(record => {
        // Search in user name and email
        const userNameMatch = record.user.full_name.toLowerCase().includes(searchLower);
        const userEmailMatch = record.user.email.toLowerCase().includes(searchLower);
        
        // Search in property names
        const propertyMatch = record.properties.some(property => 
          property.name.toLowerCase().includes(searchLower) ||
          (property.defaultUri !== '-' && property.defaultUri.toLowerCase().includes(searchLower)) ||
          property.measurementId.toLowerCase().includes(searchLower)
        );

        return userNameMatch || userEmailMatch || propertyMatch;
      });
    }

    return filtered;
  }, [groupedData, searchText, filterStatus]);

  const isDataEmpty = !ga4Properties || ga4Properties.length === 0 || filteredData.length === 0;

  const handlePropertySelect = (userId, propertyId) => {
    setSelectedProperties(prev => ({ ...prev, [userId]: propertyId }));
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    
    // Notify parent component about filter change
    if (typeof onFilterChange === 'function') {
      onFilterChange({ status: value, search: searchText });
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    
    // Notify parent component about search change
    if (typeof onFilterChange === 'function') {
      onFilterChange({ status: filterStatus, search: value });
    }
  };

  const handleSyncClick = (user, property) => {
    setSelectedUser(user);
    setSelectedProperty(property);
    setSyncModalVisible(true);
  };

  const handleSyncSubmit = async (values) => {
    try {
      setSyncLoading(true);

      const [startDate, endDate] = values.date_range;

      const payload = {
        user_id: selectedUser.id,
        date_range: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
        }
      };

      const response = await force_sync_ga4_propertiesApi(payload);
      
      if (response.data && response.data.status === 'success') {
        message.success('Sync initiated successfully!');
        setSyncModalVisible(false);
        setSyncCompleted(true);
      } else {
        message.error(response.data?.message || 'Sync failed. Please try again.');
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 422) {
          message.error('Invalid date range or parameters.');
        } else if (error.response.data?.detail) {
          message.error(error.response.data.detail || 'Sync failed.');
        } else {
          message.error('Failed to initiate sync. Please try again.');
        }
      } else if (error.request) {
        message.error('No response from server. Please check your connection.');
      } else {
        message.error('An unexpected error occurred.');
      }
      console.error('Sync error:', error);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncCancel = () => {
    setSyncModalVisible(false);
  };

  const clearFilters = () => {
    setSearchText('');
    setFilterStatus('a');
    
    // Notify parent about filter clear
    if (typeof onFilterChange === 'function') {
      onFilterChange({ status: 'a', search: '' });
    }
  };

  // Desktop table columns
  const desktopColumns = [
    {
      title: 'USER',
      dataIndex: 'user',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <Space align="start">
          <UserOutlined style={{ marginTop: 4 }} />
          <Space direction="vertical" size={4}>
            <Text strong>{record.user.full_name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.user.email}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              <InfoCircleOutlined /> {record.properties.length} propert{record.properties.length === 1 ? 'y' : 'ies'}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'PROPERTY',
      dataIndex: 'selectedProperty',
      key: 'property',
      width: 250,
      render: (_, record) => (
        <Select
          value={selectedProperties[record.user.id] || record.selectedProperty.id}
          onChange={(propertyId) => handlePropertySelect(record.user.id, propertyId)}
          style={{ width: '100%' }}
          dropdownMatchSelectWidth={false}
        >
          {record.properties.map((property) => (
            <Select.Option key={property.id} value={property.id}>
              <Space direction="vertical" size={2}>
                <div>{property.name}</div>
                {property.defaultUri !== '-' && (
                  <Text type="secondary" style={{ fontSize: 11 }}>{property.defaultUri}</Text>
                )}
              </Space>
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'selectedProperty',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Badge
            status={record.selectedProperty.selected ? 'success' : 'default'}
            text={record.selectedProperty.selected ? 'Active' : 'Inactive'}
          />
          {record.selectedProperty.measurementId !== 'N/A' && (
            <Text code style={{ fontSize: 12 }}>{record.selectedProperty.measurementId}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'LAST SYNCED',
      dataIndex: 'last_synced_at',
      key: 'lastSynced',
      width: 150,
      render: (lastSynced, record) => (
        <Space direction="vertical" size={2}>
          <Space size={4}>
            <ClockCircleOutlined />
            <Text>{formatTimeAgo(lastSynced)}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>{formatLocalDate(lastSynced)}</Text>
        </Space>
      ),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<SyncOutlined />}
          onClick={() => handleSyncClick(record.user, record.selectedProperty)}
          size="small"
        >
          {screens.lg ? 'Sync' : ''}
        </Button>
      ),
    },
  ];

  // Get active/inactive counts
  const activeCount = groupedData.filter(record => record.selectedProperty?.selected === true).length;
  const inactiveCount = groupedData.filter(record => record.selectedProperty?.selected === false).length;

  return (
    <>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Header Row with Title, Search, and Radio */}
          <Row gutter={[16, 16]} align="middle">
            {/* Title Column */}
            <Col xs={24} sm={24} md={8}>
              <Space direction="vertical" size={0}>
                <Title level={5} style={{ margin: 0 }}>GA4 Properties</Title>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Users: <strong>{groupedData.length}</strong> • Properties: <strong>{groupedData.reduce((acc, user) => acc + user.properties.length, 0)}</strong>
                  {(searchText || filterStatus !== 'a') && (
                    <span> • Filtered: <strong>{filteredData.length}</strong></span>
                  )}
                </Paragraph>
              </Space>
            </Col>

            {/* Search Column */}
            <Col xs={24} sm={24} md={8}>
              <Input
                placeholder="Search by user name, email, or property..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={handleSearchChange}
                allowClear
                size={screens.xs ? 'small' : 'middle'}
              />
            </Col>

            {/* Radio Group Column */}
            <Col xs={24} sm={24} md={8}>
              <div style={{ textAlign: screens.md ? 'right' : 'left' }}>
                <Radio.Group 
                  value={filterStatus} 
                  onChange={handleStatusFilterChange}
                  size={screens.xs ? 'small' : 'middle'}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="a">ALL ({groupedData.length})</Radio.Button>
                  <Radio.Button value="b">ACTIVE ({activeCount})</Radio.Button>
                  <Radio.Button value="c">INACTIVE ({inactiveCount})</Radio.Button>
                </Radio.Group>
              </div>
            </Col>
          </Row>

          {/* Active Filters Display */}
          {(searchText || filterStatus !== 'a') && (
            <Row>
              <Col span={24}>
                <Space size={8} wrap>
                  <Text type="secondary" style={{ fontSize: 12 }}>Active filters:</Text>
                  {filterStatus !== 'a' && (
                    <Tag 
                      closable 
                      onClose={() => {
                        setFilterStatus('a');
                        if (typeof onFilterChange === 'function') {
                          onFilterChange({ status: 'a', search: searchText });
                        }
                      }}
                      style={{ marginRight: 0 }}
                    >
                      Status: {filterStatus === 'b' ? 'Active' : 'Inactive'}
                    </Tag>
                  )}
                  {searchText && (
                    <Tag 
                      closable 
                      onClose={() => {
                        setSearchText('');
                        if (typeof onFilterChange === 'function') {
                          onFilterChange({ status: filterStatus, search: '' });
                        }
                      }}
                      style={{ marginRight: 0 }}
                    >
                      Search: "{searchText}"
                    </Tag>
                  )}
                  {(searchText || filterStatus !== 'a') && (
                    <Button 
                      type="link" 
                      size="small" 
                      onClick={clearFilters}
                      style={{ padding: 0 }}
                    >
                      Clear all
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          )}

          {/* Content Area */}
          {isDataEmpty ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Title level={4}>
                {searchText || filterStatus !== 'a' ? 'No Matching Results Found' : 'No GA4 Properties Found'}
              </Title>
              <Paragraph type="secondary">
                {searchText && `No properties or users match "${searchText}". `}
                {filterStatus === 'b' && 'No active properties found. '}
                {filterStatus === 'c' && 'No inactive properties found. '}
                Try adjusting your filters.
              </Paragraph>
              {(searchText || filterStatus !== 'a') && (
                <Button onClick={clearFilters} type="primary">
                  Clear All Filters
                </Button>
              )}
            </div>
          ) : (
            <>
              {!screens.md ? (
                <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '4px' }}>
                  {filteredData.map(record => (
                    <MobileCard
                      key={record.user.id}
                      record={record}
                      selectedProperties={selectedProperties}
                      onPropertySelect={handlePropertySelect}
                      onSyncClick={handleSyncClick}
                      formatTimeAgo={formatTimeAgo}
                    />
                  ))}
                </div>
              ) : (
                <Table
                  dataSource={filteredData}
                  columns={desktopColumns}
                  rowKey={record => record.user.id}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    size: 'small',
                  }}
                  size="small"
                  scroll={{ x: screens.lg ? undefined : 800 }}
                />
              )}
            </>
          )}
        </Space>
      </Card>

      {/* Sync Modal Component */}
      <SyncModal
        visible={syncModalVisible}
        onCancel={handleSyncCancel}
        onSubmit={handleSyncSubmit}
        selectedUser={selectedUser}
        selectedProperty={selectedProperty}
        loading={syncLoading}
        screens={screens}
      />
    </>
  );
};